import { useEcho } from '@laravel/echo-vue';
import { ref } from 'vue';

export type RunStatus =
    | 'pending'
    | 'running'
    | 'completed'
    | 'failed'
    | 'cancelled';

export type RunEvent = {
    type: string;
    payload: Record<string, unknown>;
    timestamp: string;
};

export function useRunProgress(runId: string | number) {
    const progress = ref<number>(0);
    const events = ref<RunEvent[]>([]);
    const status = ref<RunStatus>('pending');
    const currentPaperTitle = ref<string | null>(null);
    const papersProcessed = ref<number>(0);
    const totalPapers = ref<number>(0);

    useEcho(
        `run.${runId}`,
        [
            '.run.progress_updated',
            '.run.started',
            '.run.completed',
            '.run.failed',
            '.run.cancelled',
        ],
        (e: Record<string, unknown>) => {
            const event = e.event as string | undefined;
            const data = (e.data ?? {}) as Record<string, unknown>;

            events.value.push({
                type: event ?? 'unknown',
                payload: e,
                timestamp: new Date().toISOString(),
            });

            if (event === 'run.progress_updated') {
                const prog = data.progress as
                    | Record<string, unknown>
                    | null
                    | undefined;

                if (prog) {
                    if (typeof prog.papers_processed === 'number') {
                        papersProcessed.value = prog.papers_processed;
                    }

                    if (typeof prog.total_papers === 'number') {
                        totalPapers.value = prog.total_papers;
                    }

                    if (typeof prog.current_paper_title === 'string') {
                        currentPaperTitle.value = prog.current_paper_title;
                    }

                    if (totalPapers.value > 0) {
                        progress.value = Math.round(
                            (papersProcessed.value / totalPapers.value) * 100,
                        );
                    }
                }

                if (data.status) {
                    status.value = data.status as RunStatus;
                }
            }

            if (event === 'run.completed') {
                status.value = 'completed';
                progress.value = 100;
                currentPaperTitle.value = null;
            }

            if (event === 'run.failed') {
                status.value = 'failed';
                currentPaperTitle.value = null;
            }

            if (event === 'run.cancelled') {
                status.value = 'cancelled';
                currentPaperTitle.value = null;
            }

            if (event === 'run.started') {
                status.value = 'running';
            }
        },
    );

    return {
        progress,
        events,
        status,
        currentPaperTitle,
        papersProcessed,
        totalPapers,
    };
}
